/**
 * Azure KPI Troubleshooting Knowledge Base
 * AIDLC Administrative Backend
 *
 * Comprehensive status explanations, troubleshooting steps, and Azure portal
 * deep links for every KPI section on the Azure Dashboard tab.
 *
 * Used by DeploymentPipelinePage.tsx to render expandable troubleshooting
 * panels when a KPI status indicates an issue.
 */

import type { AzureKpiData } from '../../types/deployment';

// ── Types ─────────────────────────────────────────────────────────

export type TroubleshootSeverity = 'healthy' | 'info' | 'warning' | 'critical';

export interface TroubleshootStep {
  action: string;
  detail: string;
  type: 'portal' | 'cli' | 'check';
}

export interface TroubleshootInfo {
  severity: TroubleshootSeverity;
  title: string;
  explanation: string;
  possibleCauses: string[];
  steps: TroubleshootStep[];
  portalUrl: string;
  cliCommand?: string;
}

// ── Azure Portal Base URL ─────────────────────────────────────────

// Azure Portal deep-link components — subscription ID is not a secret but we avoid
// hardcoding it in multiple places. These are only used for constructing portal URLs
// for admin users who already have portal access.
const AZURE_TENANT = 'ldaja.com';
const AZURE_SUB_ID = '4ac240b2-10c1-4a54-9438-33a59bece8fb';
const AZURE_RG = 'rg-ldaja-aidlc-prod';
const AZURE_APP = 'app-ldaja-aidlc-web';

const PORTAL = `https://portal.azure.com/#@${AZURE_TENANT}`;
const RG = `/subscriptions/${AZURE_SUB_ID}/resourceGroups/${AZURE_RG}`;
const APP_SERVICE = `${RG}/providers/Microsoft.Web/sites/${AZURE_APP}`;

// ── App Service Status ────────────────────────────────────────────

function evaluateAppService(data: AzureKpiData): TroubleshootInfo | null {
  const { serviceHealth } = data;
  const state = serviceHealth.state?.toLowerCase() ?? 'unknown';

  if (state === 'running') return null; // healthy

  if (state === 'unknown') {
    return {
      severity: 'warning',
      title: 'App Service State is Unknown',
      explanation:
        'Azure cannot determine the current state of your App Service. This typically means the Azure Resource Manager could not reach the App Service infrastructure to verify its status. ' +
        'The app may still be running normally — "Unknown" refers to Azure\'s ability to read the status, not necessarily the app itself.',
      possibleCauses: [
        'The App Service is transitioning between states (starting, stopping, or restarting)',
        'Azure Resource Manager is experiencing a temporary communication issue with the App Service infrastructure',
        'A platform update or maintenance window is occurring in the West US 2 region',
        'The App Service Plan (B1 tier) is being scaled or moved to a different worker',
        'The Azure CLI or API returned stale or incomplete data during the KPI fetch',
      ],
      steps: [
        {
          action: 'Check the Azure Portal directly',
          detail: 'Open App Services > app-ldaja-aidlc-web > Overview. The portal may show a more up-to-date status than the CLI.',
          type: 'portal',
        },
        {
          action: 'Hit the health endpoint manually',
          detail: 'Visit https://ldaja.ai/api/health in your browser. If it responds with 200 OK, the app is running fine regardless of what Azure reports.',
          type: 'check',
        },
        {
          action: 'Check Azure Service Health',
          detail: 'Look at Service Health > Service Issues in the Azure Portal to see if Microsoft has reported any ongoing issues in West US 2.',
          type: 'portal',
        },
        {
          action: 'Restart the App Service',
          detail: 'If the health endpoint is also unresponsive, try restarting: az webapp restart --name app-ldaja-aidlc-web --resource-group rg-ldaja-aidlc-prod',
          type: 'cli',
        },
        {
          action: 'Check Activity Log',
          detail: 'Azure Portal > App Service > Activity Log. Look for recent "Restart", "Stop", or platform events in the last hour.',
          type: 'portal',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/appServices`,
      cliCommand: 'az webapp show --name app-ldaja-aidlc-web --resource-group rg-ldaja-aidlc-prod --query state -o tsv',
    };
  }

  if (state === 'stopped') {
    return {
      severity: 'critical',
      title: 'App Service is Stopped',
      explanation:
        'The App Service has been stopped — either manually by an administrator or automatically by Azure. ' +
        'While stopped, the website at ldaja.ai is completely unavailable. Users will see an error page. ' +
        'No billing occurs for compute while stopped, but the app will not serve any requests.',
      possibleCauses: [
        'Someone manually stopped the App Service through the Azure Portal or CLI',
        'The App Service Plan ran out of quota (unlikely on B1 paid tier, but possible with Free/Shared tiers)',
        'An automated scaling rule or policy stopped the service',
        'A deployment script stopped the app and failed to restart it',
      ],
      steps: [
        {
          action: 'Start the App Service immediately',
          detail: 'Azure Portal > App Services > app-ldaja-aidlc-web > Overview > Click "Start" button at the top.',
          type: 'portal',
        },
        {
          action: 'Or start via CLI',
          detail: 'az webapp start --name app-ldaja-aidlc-web --resource-group rg-ldaja-aidlc-prod',
          type: 'cli',
        },
        {
          action: 'Check who stopped it',
          detail: 'Azure Portal > App Service > Activity Log. Filter by "Stop Web App" operation to see who/what triggered the stop.',
          type: 'portal',
        },
        {
          action: 'Verify the site is back',
          detail: 'After starting, visit https://ldaja.ai/api/health and confirm a 200 OK response.',
          type: 'check',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/appServices`,
      cliCommand: 'az webapp start --name app-ldaja-aidlc-web --resource-group rg-ldaja-aidlc-prod',
    };
  }

  if (state === 'starting') {
    return {
      severity: 'info',
      title: 'App Service is Starting Up',
      explanation:
        'The App Service is currently booting up. This is normal after a restart or deployment. ' +
        'The Node.js server needs time to initialize, load configuration, and begin accepting requests. ' +
        'Typical cold start time is 20-40 seconds on the B1 plan.',
      possibleCauses: [
        'A deployment was just completed and the app is restarting with the new code',
        'Someone manually restarted the App Service',
        'The App Service auto-healed after detecting an unresponsive process',
        'The platform moved the app to a new worker instance',
      ],
      steps: [
        {
          action: 'Wait 30-60 seconds',
          detail: 'The app should finish starting within a minute. Refresh this dashboard to check the updated status.',
          type: 'check',
        },
        {
          action: 'If it stays in "Starting" for more than 2 minutes',
          detail: 'Check the App Service logs: Azure Portal > App Service > Log Stream. Look for startup errors.',
          type: 'portal',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/logStream`,
    };
  }

  if (state === 'stopping') {
    return {
      severity: 'warning',
      title: 'App Service is Shutting Down',
      explanation:
        'The App Service is in the process of shutting down. Once stopped, the website will be unavailable. ' +
        'If you did not initiate this, someone else or an automated process may have triggered it.',
      possibleCauses: [
        'An administrator is stopping the app for maintenance',
        'A deployment script is cycling the app',
        'An automated policy or alert action triggered a stop',
      ],
      steps: [
        {
          action: 'Check if this was intentional',
          detail: 'Azure Portal > App Service > Activity Log. Look for "Stop Web App" operations.',
          type: 'portal',
        },
        {
          action: 'Restart if unintentional',
          detail: 'Once it fully stops, start it again: az webapp start --name app-ldaja-aidlc-web --resource-group rg-ldaja-aidlc-prod',
          type: 'cli',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/activityLog`,
    };
  }

  // Any other unknown state — sanitize to prevent rendering unexpected content
  const sanitizedState = String(serviceHealth.state).slice(0, 50).replace(/[<>"'&]/g, '');
  return {
    severity: 'warning',
    title: `App Service State: "${sanitizedState}"`,
    explanation:
      `The App Service is reporting an unexpected state: "${sanitizedState}". ` +
      'This is not a standard Azure App Service state and may indicate a transitional or error condition.',
    possibleCauses: [
      'Azure API returned a non-standard state string',
      'A platform update changed the state enum',
      'The KPI fetch encountered a partial response',
    ],
    steps: [
      {
        action: 'Check the Azure Portal',
        detail: 'Open App Services > app-ldaja-aidlc-web > Overview for the authoritative status.',
        type: 'portal',
      },
      {
        action: 'Refresh the dashboard',
        detail: 'Click "Refresh" above to fetch fresh data from Azure.',
        type: 'check',
      },
    ],
    portalUrl: `${PORTAL}/resource${APP_SERVICE}/appServices`,
    cliCommand: 'az webapp show --name app-ldaja-aidlc-web --resource-group rg-ldaja-aidlc-prod --query state -o tsv',
  };
}

// ── Health Check ──────────────────────────────────────────────────

function evaluateHealthCheck(data: AzureKpiData): TroubleshootInfo | null {
  const { healthCheck } = data;

  if (healthCheck.statusCode === 200) return null;

  if (!healthCheck.available || healthCheck.statusCode === undefined || healthCheck.statusCode === null) {
    return {
      severity: 'warning',
      title: 'Health Check Unavailable',
      explanation:
        'The health check endpoint could not be reached or returned no status code. ' +
        'This means we cannot confirm whether the application at ldaja.ai is responding to requests.',
      possibleCauses: [
        'The App Service is stopped, starting, or restarting',
        'The /api/health endpoint was removed or renamed',
        'Network connectivity issue between the KPI fetcher and the App Service',
        'The backend Node.js process crashed and hasn\'t auto-restarted yet',
      ],
      steps: [
        {
          action: 'Try the health endpoint manually',
          detail: 'Open https://ldaja.ai/api/health in a new browser tab. If it loads with {"status":"ok"}, the app is fine.',
          type: 'check',
        },
        {
          action: 'Check App Service status',
          detail: 'Confirm the App Service is in "Running" state in the Azure Portal.',
          type: 'portal',
        },
        {
          action: 'Check application logs',
          detail: 'Azure Portal > App Service > Log Stream. Look for crash/restart messages.',
          type: 'portal',
        },
        {
          action: 'Restart if needed',
          detail: 'az webapp restart --name app-ldaja-aidlc-web --resource-group rg-ldaja-aidlc-prod',
          type: 'cli',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/logStream`,
      cliCommand: 'curl -s -o /dev/null -w "%{http_code}" https://ldaja.ai/api/health',
    };
  }

  if (healthCheck.statusCode >= 500) {
    return {
      severity: 'critical',
      title: `Health Check Failed (${healthCheck.statusCode})`,
      explanation:
        `The health endpoint returned a ${healthCheck.statusCode} server error. ` +
        'This indicates the application server is running but encountering an internal error. ' +
        'Users may experience errors or degraded functionality.',
      possibleCauses: [
        'An unhandled exception in the application startup code',
        'A required environment variable is missing or misconfigured (check .env.production)',
        'A database or external service dependency is unavailable',
        'The Node.js process ran out of memory (check memory usage KPI)',
        'A recent deployment introduced a bug in the health check route',
      ],
      steps: [
        {
          action: 'Check application logs immediately',
          detail: 'Azure Portal > App Service > Log Stream. The error stack trace should be visible.',
          type: 'portal',
        },
        {
          action: 'Check recent deployments',
          detail: 'If this started after a deploy, the new code may have a bug. Consider rolling back.',
          type: 'check',
        },
        {
          action: 'Verify environment variables',
          detail: 'Azure Portal > App Service > Configuration > Application Settings. Ensure all required vars are set.',
          type: 'portal',
        },
        {
          action: 'Restart the App Service',
          detail: 'A restart may resolve transient issues: az webapp restart --name app-ldaja-aidlc-web --resource-group rg-ldaja-aidlc-prod',
          type: 'cli',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/logStream`,
    };
  }

  if (healthCheck.statusCode >= 400 && healthCheck.statusCode < 500) {
    return {
      severity: 'warning',
      title: `Health Check Returned ${healthCheck.statusCode}`,
      explanation:
        `The health endpoint returned a ${healthCheck.statusCode} client error. ` +
        'This is unusual for a health check and suggests the endpoint routing may be misconfigured.',
      possibleCauses: [
        'The /api/health route was removed or changed',
        'Authentication middleware is blocking the health check request',
        'A proxy or CDN is intercepting the request',
      ],
      steps: [
        {
          action: 'Check the health endpoint URL',
          detail: 'Verify https://ldaja.ai/api/health returns the expected JSON response.',
          type: 'check',
        },
        {
          action: 'Review server routes',
          detail: 'Check server/src/index.ts to ensure the /api/health route is properly registered.',
          type: 'check',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/appServices`,
    };
  }

  return null;
}

// ── SSL Certificate ───────────────────────────────────────────────

function evaluateSSL(data: AzureKpiData): TroubleshootInfo | null {
  const { sslStatus } = data;

  if (!sslStatus.available) {
    return {
      severity: 'warning',
      title: 'SSL Status Unavailable',
      explanation:
        'Could not retrieve SSL certificate information. This doesn\'t necessarily mean the certificate is invalid — ' +
        'it may just mean the status check failed.',
      possibleCauses: [
        'Network timeout when checking the certificate',
        'The certificate check CLI command failed',
      ],
      steps: [
        {
          action: 'Check manually',
          detail: 'Click the lock icon next to https://ldaja.ai in your browser to view certificate details.',
          type: 'check',
        },
        {
          action: 'Refresh the dashboard',
          detail: 'Click "Refresh" to retry the SSL check.',
          type: 'check',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/customDomains`,
    };
  }

  if (sslStatus.isValid === false) {
    return {
      severity: 'critical',
      title: 'SSL Certificate is Invalid or Expired',
      explanation:
        'The SSL certificate for ldaja.ai is either expired or invalid. Users will see a security warning in their browser ' +
        'and may not be able to access the site. This is a high-priority issue that affects all users.',
      possibleCauses: [
        'The certificate has expired and was not auto-renewed',
        'The certificate was issued for a different domain name',
        'Azure managed certificate renewal failed',
        'A manual certificate was uploaded incorrectly',
      ],
      steps: [
        {
          action: 'Check certificate details in Azure Portal',
          detail: 'App Service > Custom domains > SSL binding. Check the expiration date and domain match.',
          type: 'portal',
        },
        {
          action: 'Renew the certificate',
          detail: 'If using Azure managed certificate, delete and re-create the binding. If using a custom cert, upload a new one.',
          type: 'portal',
        },
        {
          action: 'Verify in browser',
          detail: 'Visit https://ldaja.ai and check the lock icon for certificate details.',
          type: 'check',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/customDomains`,
    };
  }

  const daysLeft = sslStatus.daysUntilExpiry ?? 999;
  if (daysLeft <= 30) {
    return {
      severity: daysLeft <= 7 ? 'critical' : 'warning',
      title: `SSL Certificate Expires in ${daysLeft} Days`,
      explanation:
        `The SSL certificate for ldaja.ai will expire in ${daysLeft} days (${sslStatus.validTo ?? 'unknown date'}). ` +
        (daysLeft <= 7
          ? 'This is urgent — if the certificate expires, users will see browser security warnings and may not be able to access the site.'
          : 'You should renew it before it expires to avoid any disruption.'),
      possibleCauses: [
        'Azure managed certificate auto-renewal has not triggered yet',
        'DNS validation required for renewal is failing',
        'The certificate was manually provisioned and needs manual renewal',
      ],
      steps: [
        {
          action: 'Check auto-renewal status',
          detail: 'Azure Portal > App Service > Custom domains. Azure managed certs auto-renew ~30 days before expiry.',
          type: 'portal',
        },
        {
          action: 'Verify DNS validation',
          detail: 'Ensure the CNAME or TXT records for certificate validation are still in place in Azure DNS.',
          type: 'portal',
        },
        {
          action: 'Force renewal if needed',
          detail: 'Delete the SSL binding and re-create it to trigger a fresh certificate issuance.',
          type: 'portal',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/customDomains`,
    };
  }

  return null;
}

// ── DNS Status ────────────────────────────────────────────────────

function evaluateDNS(data: AzureKpiData): TroubleshootInfo | null {
  const { dnsStatus } = data;

  if (!dnsStatus.available) {
    return {
      severity: 'warning',
      title: 'DNS Status Unavailable',
      explanation: 'Could not check DNS resolution for ldaja.ai. The domain may still be working normally.',
      possibleCauses: ['DNS lookup command timed out', 'Network connectivity issue during the check'],
      steps: [
        {
          action: 'Test DNS manually',
          detail: 'Try visiting https://ldaja.ai in your browser. If it loads, DNS is working.',
          type: 'check',
        },
      ],
      portalUrl: `${PORTAL}/resource${RG}/providers/Microsoft.Network/dnszones/ldaja.ai/overview`,
    };
  }

  if (!dnsStatus.allHealthy) {
    const failedCount = (dnsStatus.totalCount ?? 4) - (dnsStatus.okCount ?? 0);
    return {
      severity: failedCount >= 3 ? 'critical' : 'warning',
      title: `${failedCount} DNS Nameserver${failedCount > 1 ? 's' : ''} Not Responding`,
      explanation:
        `${dnsStatus.okCount ?? 0} of ${dnsStatus.totalCount ?? 4} Azure DNS nameservers are responding correctly. ` +
        (failedCount >= 3
          ? 'With most nameservers down, some users may not be able to reach ldaja.ai. This is a significant issue.'
          : 'With most nameservers still healthy, users should not be affected. However, this should be investigated.'),
      possibleCauses: [
        'Azure DNS is experiencing a partial outage in one or more regions',
        'Nameserver records were accidentally modified',
        'Network routing issues affecting specific nameserver IPs',
        'The DNS zone delegation at the domain registrar is incorrect',
      ],
      steps: [
        {
          action: 'Check Azure DNS zone',
          detail: 'Azure Portal > DNS zones > ldaja.ai. Verify the NS records match the expected Azure nameservers.',
          type: 'portal',
        },
        {
          action: 'Check Azure Service Health',
          detail: 'Azure Portal > Service Health > Service Issues. Look for any DNS-related incidents.',
          type: 'portal',
        },
        {
          action: 'Test DNS resolution',
          detail: 'nslookup ldaja.ai ns1-08.azure-dns.com — run this for each nameserver to isolate the failing ones.',
          type: 'cli',
        },
        {
          action: 'Verify domain registrar settings',
          detail: 'Log into your domain registrar and confirm the NS records point to the Azure nameservers listed in the DNS zone.',
          type: 'check',
        },
      ],
      portalUrl: `${PORTAL}/resource${RG}/providers/Microsoft.Network/dnszones/ldaja.ai/overview`,
      cliCommand: 'az network dns zone show --name ldaja.ai --resource-group rg-ldaja-aidlc-prod',
    };
  }

  return null;
}

// ── Performance: CPU ──────────────────────────────────────────────

function evaluateCPU(data: AzureKpiData): TroubleshootInfo | null {
  const cpu = data.performanceMetrics.cpu?.current ?? 0;

  if (cpu < 60) return null;

  const severity: TroubleshootSeverity = cpu >= 90 ? 'critical' : cpu >= 80 ? 'warning' : 'info';

  return {
    severity,
    title: `CPU Usage at ${cpu}%`,
    explanation:
      cpu >= 90
        ? `CPU usage is critically high at ${cpu}%. The server is under heavy load and response times will be significantly degraded. Users may experience timeouts and errors.`
        : cpu >= 80
          ? `CPU usage is elevated at ${cpu}%. The server is working harder than usual. While still functional, performance may be degraded under additional load.`
          : `CPU usage is moderately high at ${cpu}%. This is worth monitoring but not yet a problem. The B1 plan has 1 CPU core.`,
    possibleCauses: [
      'A traffic spike is generating more requests than usual',
      'A background job or scheduled task is consuming CPU',
      'A recent code change introduced an inefficient algorithm or infinite loop',
      'The B1 plan (1 core) may be undersized for current traffic',
      'Memory pressure is causing excessive garbage collection',
    ],
    steps: [
      {
        action: 'Check current traffic',
        detail: 'Look at the "Requests (Last Hour)" metric on this dashboard. Is it higher than usual?',
        type: 'check',
      },
      {
        action: 'View process explorer',
        detail: 'Azure Portal > App Service > Advanced Tools (Kudu) > Process Explorer. See which processes are consuming CPU.',
        type: 'portal',
      },
      {
        action: 'Check for recent deployments',
        detail: 'If CPU spiked after a deploy, the new code may have a performance issue. Consider rolling back.',
        type: 'check',
      },
      {
        action: 'Scale up if needed',
        detail: 'Azure Portal > App Service > Scale Up. Moving from B1 to B2 doubles the CPU resources.',
        type: 'portal',
      },
    ],
    portalUrl: `${PORTAL}/resource${APP_SERVICE}/metrics`,
    cliCommand: 'az monitor metrics list --resource ' + APP_SERVICE.slice(1) + ' --metric CpuPercentage --interval PT1H',
  };
}

// ── Performance: Memory ───────────────────────────────────────────

function evaluateMemory(data: AzureKpiData): TroubleshootInfo | null {
  const mem = data.performanceMetrics.memory?.current ?? 0;

  if (mem < 60) return null;

  const severity: TroubleshootSeverity = mem >= 90 ? 'critical' : mem >= 80 ? 'warning' : 'info';

  return {
    severity,
    title: `Memory Usage at ${mem}%`,
    explanation:
      mem >= 90
        ? `Memory usage is critically high at ${mem}%. The Node.js process may crash with an "out of memory" error, which would cause the app to restart and briefly be unavailable.`
        : mem >= 80
          ? `Memory usage is elevated at ${mem}%. The B1 plan has 1.75 GB total. If usage continues to climb, the process may be killed by the OS.`
          : `Memory usage is moderately high at ${mem}%. Worth monitoring to ensure it doesn't trend upward.`,
    possibleCauses: [
      'A memory leak in the application code (common with event listeners not being cleaned up)',
      'Large data sets being loaded into memory all at once',
      'Chat message history or logs accumulating without limits',
      'The in-memory store growing too large',
      'The B1 plan (1.75 GB) may be undersized for the workload',
    ],
    steps: [
      {
        action: 'Check memory trend',
        detail: 'Azure Portal > App Service > Metrics > Memory Percentage. Look at the 24-hour trend — is it gradually increasing (leak) or suddenly spiked?',
        type: 'portal',
      },
      {
        action: 'Restart to reclaim memory',
        detail: 'If urgent, restart the app: az webapp restart. This reclaims all memory but causes ~30s of downtime.',
        type: 'cli',
      },
      {
        action: 'Check Kudu process details',
        detail: 'Azure Portal > App Service > Advanced Tools > Process Explorer. Check the "Private Memory" column.',
        type: 'portal',
      },
      {
        action: 'Consider scaling up',
        detail: 'Azure Portal > App Service > Scale Up. B2 offers 3.5 GB RAM (double the B1).',
        type: 'portal',
      },
    ],
    portalUrl: `${PORTAL}/resource${APP_SERVICE}/metrics`,
  };
}

// ── Performance: Response Time ────────────────────────────────────

function evaluateResponseTime(data: AzureKpiData): TroubleshootInfo | null {
  const rt = data.performanceMetrics.responseTime ?? 0;

  if (rt < 500) return null;

  const severity: TroubleshootSeverity = rt >= 2000 ? 'critical' : rt >= 1000 ? 'warning' : 'info';

  return {
    severity,
    title: `Average Response Time: ${rt}ms`,
    explanation:
      rt >= 2000
        ? `Response time is critically slow at ${rt}ms. Users are experiencing noticeable delays loading pages. Target is under 500ms.`
        : rt >= 1000
          ? `Response time is elevated at ${rt}ms. Pages are loading slower than expected. Target is under 500ms.`
          : `Response time is slightly above target at ${rt}ms. Target is under 500ms.`,
    possibleCauses: [
      'High CPU or memory usage is slowing down request processing',
      'A slow external API call or database query is blocking responses',
      'The App Service cold-started recently and is still warming up',
      'A traffic spike is overwhelming the single B1 instance',
      'Network latency between the client and West US 2 datacenter',
    ],
    steps: [
      {
        action: 'Check CPU and Memory',
        detail: 'Look at the performance metrics on this dashboard. High CPU or memory often causes slow responses.',
        type: 'check',
      },
      {
        action: 'Check Application Insights',
        detail: 'Azure Portal > Application Insights > Performance. See which requests are slowest.',
        type: 'portal',
      },
      {
        action: 'Check for recent deployments',
        detail: 'A recent code change may have introduced a slow query or blocking operation.',
        type: 'check',
      },
    ],
    portalUrl: `${PORTAL}/resource${APP_SERVICE}/performance`,
  };
}

// ── Performance: 5xx Errors ───────────────────────────────────────

function evaluateServerErrors(data: AzureKpiData): TroubleshootInfo | null {
  const http5xx = data.performanceMetrics.http5xx ?? 0;
  const totalRequests = data.performanceMetrics.requests || 1;

  if (http5xx === 0) return null;

  const errorRate = (http5xx / totalRequests) * 100;
  const severity: TroubleshootSeverity = errorRate >= 5 ? 'critical' : errorRate >= 1 ? 'warning' : 'info';

  return {
    severity,
    title: `${http5xx} Server Errors (5xx) in the Last Hour`,
    explanation:
      `${http5xx} out of ${totalRequests.toLocaleString()} requests (${errorRate.toFixed(1)}%) returned server errors. ` +
      (errorRate >= 5
        ? 'This is a significant error rate — many users are encountering problems.'
        : errorRate >= 1
          ? 'The error rate is elevated but most users are unaffected.'
          : 'A small number of errors occurred. This may be transient.'),
    possibleCauses: [
      'An unhandled exception in a specific API route',
      'A downstream service or database is failing intermittently',
      'The application ran out of memory and crashed (check memory usage)',
      'A recent deployment introduced a bug',
      'Rate limiting or authentication middleware is misconfigured',
    ],
    steps: [
      {
        action: 'Check Application Insights failures',
        detail: 'Azure Portal > Application Insights > Failures. See the specific error messages and stack traces.',
        type: 'portal',
      },
      {
        action: 'Check application logs',
        detail: 'Azure Portal > App Service > Log Stream. Look for error output with stack traces.',
        type: 'portal',
      },
      {
        action: 'Identify the failing endpoints',
        detail: 'Application Insights > Performance > filter by status code 5xx to see which endpoints are failing.',
        type: 'portal',
      },
    ],
    portalUrl: `${PORTAL}/resource${APP_SERVICE}/failures`,
  };
}

// ── Cost ──────────────────────────────────────────────────────────

function evaluateCost(data: AzureKpiData): TroubleshootInfo | null {
  const { costData } = data;
  const projected = costData.projectedMonthEnd ?? 0;
  const budget = 100; // $100/month budget

  if (projected <= budget) return null;

  const overAmount = projected - budget;
  const severity: TroubleshootSeverity = overAmount > 50 ? 'critical' : 'warning';

  return {
    severity,
    title: `Projected to Exceed Budget by $${overAmount.toFixed(2)}`,
    explanation:
      `At the current daily burn rate of $${(costData.dailyBurnRate ?? 0).toFixed(2)}/day, ` +
      `the projected month-end spend is $${projected.toFixed(2)} — which is $${overAmount.toFixed(2)} over the $${budget} monthly budget.`,
    possibleCauses: [
      'Increased traffic or API usage is driving up compute costs',
      'Application Insights data ingestion is higher than expected',
      'A new resource was provisioned that was not budgeted',
      'Bandwidth/data transfer costs increased',
    ],
    steps: [
      {
        action: 'Review cost breakdown',
        detail: 'Check the "Cost by Resource" section below to see which services are costing the most.',
        type: 'check',
      },
      {
        action: 'Check for unused resources',
        detail: 'Azure Portal > Cost Management > Cost Analysis. Look for resources you can downsize or delete.',
        type: 'portal',
      },
      {
        action: 'Reduce Application Insights sampling',
        detail: 'If App Insights is a top cost, consider reducing the sampling rate to lower data ingestion costs.',
        type: 'portal',
      },
      {
        action: 'Set up budget alerts',
        detail: 'Azure Portal > Cost Management > Budgets. Create an alert at 80% and 100% of budget.',
        type: 'portal',
      },
    ],
    portalUrl: `${PORTAL}/resource${RG}/costanalysis`,
  };
}

// ── App Insights ──────────────────────────────────────────────────

function evaluateAppInsights(data: AzureKpiData): TroubleshootInfo | null {
  const { appInsights } = data;

  if (!appInsights.available) {
    return {
      severity: 'info',
      title: 'Application Insights Not Configured',
      explanation:
        'Application Insights telemetry is not available. This means we cannot track failed requests, ' +
        'availability, or detailed performance data.',
      possibleCauses: [
        'The APPLICATIONINSIGHTS_CONNECTION_STRING environment variable is not set',
        'The App Insights SDK is not installed in the application',
        'The workspace was deleted or disconnected',
      ],
      steps: [
        {
          action: 'Check configuration',
          detail: 'Azure Portal > App Service > Configuration. Verify APPLICATIONINSIGHTS_CONNECTION_STRING is set.',
          type: 'portal',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/configuration`,
    };
  }

  const availability = appInsights.availabilityPercentage;
  if (availability !== null && availability !== undefined && availability < 99) {
    return {
      severity: availability < 95 ? 'critical' : 'warning',
      title: `Availability at ${availability}%`,
      explanation:
        `Application availability is ${availability}%, which is below the 99% target. ` +
        'This means some availability test probes are failing, indicating intermittent downtime.',
      possibleCauses: [
        'The application is occasionally unresponsive or returning errors',
        'Network issues between Azure monitoring probes and the App Service',
        'The app is restarting frequently due to crashes or memory issues',
      ],
      steps: [
        {
          action: 'Check availability tests',
          detail: 'Azure Portal > Application Insights > Availability. Review which test locations are failing.',
          type: 'portal',
        },
        {
          action: 'Check for restart events',
          detail: 'Azure Portal > App Service > Diagnose and solve problems > "Application restarts".',
          type: 'portal',
        },
      ],
      portalUrl: `${PORTAL}/resource${APP_SERVICE}/availability`,
    };
  }

  return null;
}

// ── Alerts ────────────────────────────────────────────────────────

function evaluateAlerts(data: AzureKpiData): TroubleshootInfo | null {
  const { recentAlerts } = data;
  if (!recentAlerts.available) return null;

  const activeAlerts = recentAlerts.alerts?.filter(a => a.status === 'Active') ?? [];
  if (activeAlerts.length === 0) return null;

  const hasCritical = activeAlerts.some(a => a.severity === 'Critical');

  return {
    severity: hasCritical ? 'critical' : 'warning',
    title: `${activeAlerts.length} Active Alert${activeAlerts.length > 1 ? 's' : ''}`,
    explanation:
      `There ${activeAlerts.length === 1 ? 'is' : 'are'} ${activeAlerts.length} unresolved alert${activeAlerts.length > 1 ? 's' : ''} ` +
      `that ${activeAlerts.length === 1 ? 'requires' : 'require'} attention. ` +
      (hasCritical ? 'At least one is marked Critical severity.' : ''),
    possibleCauses: activeAlerts.map(a => `${a.severity}: ${a.name}`),
    steps: [
      {
        action: 'Review active alerts',
        detail: 'Check the alert details in the "Recent Alert History" table below. Each alert describes what triggered it.',
        type: 'check',
      },
      {
        action: 'View in Azure Portal',
        detail: 'Azure Portal > Monitor > Alerts. Filter by resource group rg-ldaja-aidlc-prod.',
        type: 'portal',
      },
      {
        action: 'Acknowledge and investigate',
        detail: 'Click on each alert in Azure Portal to see the full trigger condition and recommended actions.',
        type: 'portal',
      },
    ],
    portalUrl: `${PORTAL}#blade/Microsoft_Azure_Monitoring/AzureMonitoringBrowseBlade/alertsV2`,
  };
}

// ── Master Evaluator ──────────────────────────────────────────────

export interface DashboardIssues {
  appService: TroubleshootInfo | null;
  healthCheck: TroubleshootInfo | null;
  ssl: TroubleshootInfo | null;
  dns: TroubleshootInfo | null;
  cpu: TroubleshootInfo | null;
  memory: TroubleshootInfo | null;
  responseTime: TroubleshootInfo | null;
  serverErrors: TroubleshootInfo | null;
  cost: TroubleshootInfo | null;
  appInsights: TroubleshootInfo | null;
  alerts: TroubleshootInfo | null;
}

export function evaluateAllKpis(data: AzureKpiData): DashboardIssues {
  return {
    appService: evaluateAppService(data),
    healthCheck: evaluateHealthCheck(data),
    ssl: evaluateSSL(data),
    dns: evaluateDNS(data),
    cpu: evaluateCPU(data),
    memory: evaluateMemory(data),
    responseTime: evaluateResponseTime(data),
    serverErrors: evaluateServerErrors(data),
    cost: evaluateCost(data),
    appInsights: evaluateAppInsights(data),
    alerts: evaluateAlerts(data),
  };
}

/** Count issues by severity */
export function countIssues(issues: DashboardIssues): { critical: number; warning: number; info: number; total: number } {
  const all = Object.values(issues).filter(Boolean) as TroubleshootInfo[];
  return {
    critical: all.filter(i => i.severity === 'critical').length,
    warning: all.filter(i => i.severity === 'warning').length,
    info: all.filter(i => i.severity === 'info').length,
    total: all.length,
  };
}

/** Generate a prompt for Todd to investigate a specific issue */
export function generateToddPrompt(issue: TroubleshootInfo): string {
  return (
    `Todd, please investigate: ${issue.title}\n\n` +
    `Severity: ${issue.severity.toUpperCase()}\n\n` +
    `Context: ${issue.explanation}\n\n` +
    `Possible causes to check:\n${issue.possibleCauses.map(c => `- ${c}`).join('\n')}\n\n` +
    `Please:\n` +
    `1. Verify the current state by running the relevant Azure CLI commands\n` +
    `2. Identify the root cause\n` +
    `3. Take corrective action if possible, or recommend next steps\n` +
    `4. Report back with your findings`
  );
}
